import { useState } from 'react';
import { adminApi } from '@/api/endpoints';
import type { Player, Team } from '@/api/types';
import { Alert, Button, Card, Spinner } from '@/components/ui';
import { useAsync } from '@/lib/useAsync';
import styles from './admin.module.css';

/** Name-Heuristik für den Zielverein (Contract §13.1: Team = Münster). */
const TARGET_TEAM_HINT = 'münster';

function resolveTargetTeam(teams: Team[]): Team | null {
  const candidates = teams.filter((t) => t.name.toLowerCase().includes(TARGET_TEAM_HINT));
  // Demo-/Testteams (z. B. "SC Preußen Münster (Demo)") nicht vor dem echten Team wählen.
  const real = candidates.find((t) => !t.name.toLowerCase().includes('demo'));
  if (real) return real;
  if (candidates[0]) return candidates[0];
  // Fallback: erstes Team (manuell angelegte Setups ohne Namens-Match).
  return teams[0] ?? null;
}

interface LoadedData {
  team: Team | null;
  players: Player[];
}

export function AdminPlayersPage() {
  const { data, loading, error, reload } = useAsync<LoadedData>(async (signal) => {
    const teams = await adminApi.listTeams(signal);
    const team = resolveTargetTeam(teams);
    const players = team ? await adminApi.listPlayers(team.id, signal) : [];
    return { team, players };
  }, []);

  if (loading) return <Spinner label="Kader wird geladen…" />;
  if (error) return <Alert>{error}</Alert>;

  const team = data?.team ?? null;
  const players = [...(data?.players ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  if (!team) {
    return (
      <Card>
        <h2>Kader</h2>
        <p className="muted">
          Kein Zielverein gefunden. Lege zunächst das Team „Münster" an oder stoße einen Sync an.
        </p>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <h2>Kader — {team.name}</h2>
        <p className="muted">
          Spieler für Tor-/Assist-Tipps. „keiner" bietet das Tipp-Formular automatisch zusätzlich
          an.
        </p>
      </Card>

      <NewPlayerCard teamId={team.id} onCreated={reload} />

      {players.length === 0 && (
        <Card>
          <p className="muted">Noch keine Spieler im Kader.</p>
        </Card>
      )}

      {players.map((p) => (
        <PlayerCard key={p.id} player={p} onChanged={reload} />
      ))}
    </>
  );
}

function NewPlayerCard({ teamId, onCreated }: { teamId: number; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      await adminApi.createPlayer({
        team_id: teamId,
        name: name.trim(),
        short_code: shortCode.trim() || null,
      });
      setSaved(`Spieler „${name.trim()}" angelegt.`);
      setName('');
      setShortCode('');
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h3 style={{ marginTop: 0 }}>Spieler hinzufügen</h3>
      <div className={styles.inlineForm}>
        <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 180px' }}>
          <span className={styles.label}>Name</span>
          <input
            className={styles.input}
            value={name}
            placeholder="z. B. Joel Grodowski"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className={styles.field} style={{ marginBottom: 0, flex: '0 1 120px' }}>
          <span className={styles.label}>Kürzel (optional)</span>
          <input
            className={styles.input}
            value={shortCode}
            placeholder="z. B. GRO"
            onChange={(e) => setShortCode(e.target.value)}
          />
        </label>
        <Button
          className={styles.btnSm}
          disabled={busy || name.trim() === ''}
          onClick={() => void create()}
        >
          {busy ? '…' : 'Hinzufügen'}
        </Button>
      </div>
      {error && (
        <div style={{ marginTop: 8 }}>
          <Alert>{error}</Alert>
        </div>
      )}
      {saved && <div className={styles.savedHint}>{saved}</div>}
    </Card>
  );
}

function PlayerCard({ player, onChanged }: { player: Player; onChanged: () => void }) {
  const [name, setName] = useState(player.name);
  const [shortCode, setShortCode] = useState(player.short_code ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      await adminApi.patchPlayer(player.id, {
        name: name.trim(),
        short_code: shortCode.trim() || null,
      });
      setSaved('Gespeichert.');
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive() {
    setBusy(true);
    setError(null);
    try {
      await adminApi.patchPlayer(player.id, { is_active: !player.is_active });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Spieler „${player.name}" wirklich löschen?`)) return;
    setBusy(true);
    setError(null);
    try {
      await adminApi.deletePlayer(player.id);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className={styles.inlineForm}>
        <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 180px' }}>
          <span className={styles.label}>Name</span>
          <input
            className={styles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className={styles.field} style={{ marginBottom: 0, flex: '0 1 120px' }}>
          <span className={styles.label}>Kürzel</span>
          <input
            className={styles.input}
            value={shortCode}
            onChange={(e) => setShortCode(e.target.value)}
          />
        </label>
        <div className={styles.actions}>
          <Button
            className={styles.btnSm}
            disabled={busy || name.trim() === ''}
            onClick={() => void save()}
          >
            {busy ? '…' : 'Speichern'}
          </Button>
          <Button className={styles.btnSm} variant="ghost" disabled={busy} onClick={() => void toggleActive()}>
            {player.is_active ? 'Inaktiv setzen' : 'Aktivieren'}
          </Button>
          <Button
            className={`${styles.btnSm} ${styles.btnDanger}`}
            disabled={busy}
            onClick={() => void remove()}
          >
            Löschen
          </Button>
        </div>
      </div>
      {!player.is_active && (
        <div className={styles.counts}>
          <span className={`${styles.statusBadge} ${styles.statusDisabled}`}>inaktiv</span>
        </div>
      )}
      {error && (
        <div style={{ marginTop: 8 }}>
          <Alert>{error}</Alert>
        </div>
      )}
      {saved && <div className={styles.savedHint}>{saved}</div>}
    </Card>
  );
}
